import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jobInvitationService, JobInvitation } from '../services/jobInvitationService';
import { Calendar, Search, Trash2, Mail, X } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import useConfirmDialog from '../hooks/useConfirmDialog';
import SelectAllToggle from '../components/Common/SelectAllToggle';
import './JobInvitations.scss';

const JobInvitations: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // This is always for employees (new route is /job-invitations, legacy is /pakvietimai with mode=employee)
  const isEmployeeRoute = location.pathname === '/job-invitations' || 
                          location.pathname === '/pakvietimai';

  const [invitations, setInvitations] = useState<JobInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<JobInvitation | null>(null);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [selectedInvitations, setSelectedInvitations] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();

  useEffect(() => {
    if (isEmployeeRoute) {
      loadInvitations();
    }
  }, [isEmployeeRoute]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const data = await jobInvitationService.getReceived();
      setInvitations(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading invitations:', err);
      setError('Nepavyko įkelti kvietimų');
    } finally {
      setLoading(false);
    }
  };

  const handleViewLetter = (invitation: JobInvitation) => {
    setSelectedInvitation(invitation);
    setShowLetterModal(true);
  };

  const handleUpdateStatus = async (invitationId: number, status: 'accepted' | 'rejected') => {
    try {
      await jobInvitationService.updateStatus(invitationId, status);
      await loadInvitations();
    } catch (err: any) {
      console.error('Error updating invitation status:', err);
      showToast('Nepavyko atnaujinti kvietimo būsenos', 'error');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="status-badge pending">Laukia atsakymo</span>;
      case 'accepted':
        return <span className="status-badge accepted">Priimta</span>;
      case 'rejected':
        return <span className="status-badge rejected">Atmesta</span>;
      case 'expired':
        return <span className="status-badge expired">Pasibaigė</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filter invitations by title/company
  const filterInvitationsByTitle = (items: JobInvitation[]) => {
    if (!searchTitleFilter.trim()) return items;
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return items.filter(inv => 
      inv.job_title?.toLowerCase().includes(filterLower) ||
      inv.employer_company?.toLowerCase().includes(filterLower) ||
      inv.employer_name?.toLowerCase().includes(filterLower) ||
      inv.letter_title?.toLowerCase().includes(filterLower)
    );
  };

  const handleToggleSelectAll = () => {
    const filteredInvitations = filterInvitationsByTitle(invitations);
    if (selectedInvitations.length === filteredInvitations.length && filteredInvitations.length > 0) {
      setSelectedInvitations([]);
    } else {
      setSelectedInvitations(filteredInvitations.map(inv => inv.id!));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedInvitations(prev => 
      prev.includes(id) ? prev.filter(iid => iid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedInvitations.length === 0) return;
    if (!await confirm(`Ar tikrai norite ištrinti ${selectedInvitations.length} kvietimų?`)) return;

    try {
      // Note: You may need to implement delete functionality in the service
      // For now we'll just reject them
      await Promise.all(
        selectedInvitations.map(id => jobInvitationService.updateStatus(id, 'rejected'))
      );
      setSelectedInvitations([]);
      loadInvitations();
    } catch (err) {
      console.error('Error deleting invitations:', err);
      showToast('Įvyko klaida trinant kvietimus', 'error');
    }
  };

  const filteredInvitations = filterInvitationsByTitle(invitations);

  if (!isEmployeeRoute) {
    return (
      <div className="job-invitations-container">
        <div className="error-message">
          <p>Ši funkcija prieinama tik darbuotojams</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-invitations-container">
      {ConfirmDialogNode}
      <div className="container">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <span onClick={() => navigate('/')}>Pradžia</span>
          <span className="separator">&gt;</span>
          <span className="current">Gauti kvietimai</span>
        </div>

        {/* Header row with title and filters */}
        <div className="header-row">
          <div className="tab-title">
            Gauti kvietimai
            {invitations.length > 0 && (
              <span className="count">{invitations.length}</span>
            )}
          </div>
          <div className="filters-toolbar">
            <SelectAllToggle
              selectedCount={selectedInvitations.length}
              totalCount={filteredInvitations.length}
              onToggle={handleToggleSelectAll}
            />
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedInvitations.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Kvietimo pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading">Kraunama...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredInvitations.length === 0 ? (
          <div className="no-data">
            <Mail size={64} className="no-data-icon" />
            <p>{searchTitleFilter ? 'Kvietimų pagal šį filtrą nerasta' : 'Jūs dar nesate gavę kvietimų iš darbdavių'}</p>
          </div>
        ) : (
          <div className="invitations-list">
            {filteredInvitations.map((invitation) => (
              <div key={invitation.id} className={`invitation-card ${selectedInvitations.includes(invitation.id!) ? 'selected' : ''}`}>
                <div className="invitation-card-header">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={selectedInvitations.includes(invitation.id!)}
                      onChange={() => handleToggleSelect(invitation.id!)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="employer-info">
                    <h3>{invitation.employer_company || invitation.employer_name}</h3>
                    {invitation.employer_name && invitation.employer_company && (
                      <p className="employer-name">{invitation.employer_name}</p>
                    )}
                  </div>
                  {getStatusBadge(invitation.status)}
                </div>

              <div className="invitation-card-body">
                {invitation.job_title && (
                  <div className="job-info">
                    <h4>Pareigos</h4>
                    <p>{invitation.job_title}</p>
                  </div>
                )}

                {invitation.letter_title && (
                  <div className="letter-info">
                    <h4>Pasiūlymas</h4>
                    <p>{invitation.letter_title}</p>
                  </div>
                )}

                <div className="invitation-meta">
                  <Calendar size={16} />
                  <span>Gauta: {formatDate(invitation.sent_at!)}</span>
                </div>
              </div>

              <div className="invitation-card-footer">
                {invitation.letter_content && (
                  <button
                    className="btn-view-letter"
                    onClick={() => handleViewLetter(invitation)}
                  >
                    Skaityti laišką
                  </button>
                )}

                {invitation.status === 'pending' && (
                  <div className="invitation-actions">
                    <button
                      className="btn-accept"
                      onClick={() => handleUpdateStatus(invitation.id!, 'accepted')}
                    >
                      Priimti
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => handleUpdateStatus(invitation.id!, 'rejected')}
                    >
                      Atmesti
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {showLetterModal && selectedInvitation && (
        <div className="modal-overlay" onClick={() => setShowLetterModal(false)}>
          <div className="letter-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="letter-modal-header">
              <div>
                <h2>{selectedInvitation.letter_title || 'Kvietimo laiškas'}</h2>
                <p className="employer-name">
                  {selectedInvitation.employer_company || selectedInvitation.employer_name}
                </p>
              </div>
              <button className="btn-close" onClick={() => setShowLetterModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="letter-modal-body">
              <p>{selectedInvitation.letter_content}</p>
            </div>

            <div className="letter-modal-footer">
              <button className="btn-secondary" onClick={() => setShowLetterModal(false)}>
                Uždaryti
              </button>
              {selectedInvitation.status === 'pending' && (
                <>
                  <button
                    className="btn-reject"
                    onClick={() => {
                      handleUpdateStatus(selectedInvitation.id!, 'rejected');
                      setShowLetterModal(false);
                    }}
                  >
                    Atmesti
                  </button>
                  <button
                    className="btn-accept"
                    onClick={() => {
                      handleUpdateStatus(selectedInvitation.id!, 'accepted');
                      setShowLetterModal(false);
                    }}
                  >
                    Priimti
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobInvitations;

